import React, { useEffect, useState } from "react";
import { Card, Row, Col, Tab, Nav } from "react-bootstrap";
import JSZip from "jszip";
import { useGetCreativePreview } from "../../../../../queries/query-hooks/workspace/brand-ads-hook";
import { useParams } from "react-router-dom";
import PageNotFound from "../../../../PageNotFound";
import { findIcon } from "../../../../dashboard/icons";
import Skeleton from "../../../../../components/skeleton";

function CreativePreview() {
  const { project_id, creative_id, campaign_id } = useParams();
  const [loadingHTML, setLoadingHTML] = useState(false);

  const { data, isLoading } = useGetCreativePreview(
    project_id,
    {
      creative_id: creative_id,
      campaign_list: campaign_id,
    },
    !!creative_id && !!campaign_id
  );

  const [htmlContentMobile, setHtmlContentMobile] = useState("");
  const [htmlContentDesktop, setHtmlContentDesktop] = useState("");

  const tabs = [
    {
      title: "Desktop",
      eventKey: "Desktop",
      Content: () => (
        <IframeViewer
          html={htmlContentDesktop}
          isLoading={isLoading}
          loadingHTML={loadingHTML}
        />
      ),
    },
    {
      title: "Mobile",
      eventKey: "Mobile",
      Content: () => (
        <IframeViewer
          html={htmlContentMobile}
          isLoading={isLoading}
          loadingHTML={loadingHTML}
        />
      ),
    },
  ];

  const downloadAndExtractHTML = async (zipFileUrl) => {
    try {
      setLoadingHTML(true);
      const response = await fetch(zipFileUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch ZIP file: ${response.statusText}`);
      }
      const arrayBuffer = await response.arrayBuffer();
      const zip = await JSZip.loadAsync(arrayBuffer);

      let htmlContent = null;
      let htmlFilename = null;

      for (let filename in zip.files) {
        if (filename.endsWith(".html") && !filename.includes("/")) {
          htmlFilename = filename;
          const file = zip.files[filename];
          htmlContent = await file.async("text");
          break;
        }
      }

      if (!htmlContent) {
        throw new Error("No HTML file found in the ZIP archive");
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, "text/html");

      const resolvePath = (base, path) => {
        if (path.startsWith("/")) return path.slice(1);
        const stack = base.split("/");
        const parts = path.split("/");
        stack.pop();
        for (let i = 0; i < parts.length; i++) {
          if (parts[i] === ".") continue;
          if (parts[i] === "..") stack.pop();
          else stack.push(parts[i]);
        }
        return stack.join("/");
      };

      const cssPromises = Array.from(
        doc.querySelectorAll('link[rel="stylesheet"]')
      ).map(async (link) => {
        const cssFile = resolvePath(htmlFilename, link.getAttribute("href"));
        const cssContent = await zip.file(cssFile)?.async("text");
        if (cssContent) {
          const style = doc.createElement("style");
          style.textContent = cssContent;
          link.parentNode.replaceChild(style, link);
        }
      });

      const jsPromises = Array.from(doc.querySelectorAll("script[src]")).map(
        async (script) => {
          const jsFile = resolvePath(htmlFilename, script.getAttribute("src"));
          const jsContent = await zip.file(jsFile)?.async("text");
          if (jsContent) {
            const newScript = doc.createElement("script");
            newScript.textContent = jsContent;
            script.parentNode.replaceChild(newScript, script);
          }
        }
      );

      const stylePromises = Array.from(doc.querySelectorAll("style")).map(
        async (style) => {
          let cssContent = style.textContent;
          const importRegex =
            /@import\s+(?:url\(\s*)?['"]?([^'")]+)['"]?\s*\)?;/g;
          let match;
          while ((match = importRegex.exec(cssContent)) !== null) {
            const importPath = resolvePath(htmlFilename, match[1]);
            const importedCss = await zip.file(importPath)?.async("text");
            if (importedCss) {
              cssContent = cssContent.replace(match[0], importedCss);
            }
          }
          style.textContent = cssContent;
        }
      );

      const imgPromises = Array.from(doc.querySelectorAll("img")).map(
        async (img) => {
          const imgFile = resolvePath(htmlFilename, img.getAttribute("src"));
          const imgContent = await zip.file(imgFile)?.async("arraybuffer");
          if (imgContent) {
            const blob = new Blob([imgContent]);
            const reader = new FileReader();
            const dataUrl = await new Promise((resolve) => {
              reader.onloadend = () => resolve(reader.result);
              reader.readAsDataURL(blob);
            });
            img.src = dataUrl;
          }
        }
      );

      await Promise.all([
        ...cssPromises,
        ...jsPromises,
        ...stylePromises,
        ...imgPromises,
      ]);

      return doc.documentElement.outerHTML;
    } catch (error) {
      console.error(error);
      return null;
    } finally {
      setLoadingHTML(false);
    }
  };

  useEffect(() => {
    if (data && Array.isArray(data) && data.length > 0) {
      downloadAndExtractHTML(data[0]?.link).then((htmlContent) => {
        if (htmlContent) {
          setHtmlContentMobile(htmlContent);
        } else {
          console.log("Failed to extract HTML content");
        }
      });

      downloadAndExtractHTML(data[1]?.link).then((htmlContent) => {
        if (htmlContent) {
          setHtmlContentDesktop(htmlContent);
        } else {
          console.log("Failed to extract HTML content");
        }
      });
    }
  }, [data]);

  return (
    <>
      <Row>
        <Col md={12} sm={12}></Col>
        <Col md={12}>
          <HorizontalTab tabs={tabs} />
        </Col>
      </Row>
    </>
  );
}

export default CreativePreview;

function HorizontalTab({ tabs }) {
  return (
    <>
      <Tab.Container defaultActiveKey={tabs[0].eventKey}>
        <Nav className="bg-transparent d-flex align-items-center">
          <div
            className="mb-0 nav nav-tabs bg-transparent fs-6 gap-3 "
            id="nav-tab1"
            role="tablist"
          >
            {tabs?.length > 0 &&
              tabs?.map(({ title, eventKey }) => (
                <Nav.Link
                  key={eventKey}
                  className="px-3 py-1 rounded-pill text-decoration-none border border-secondary"
                  eventKey={eventKey}
                  data-bs-toggle="tab"
                  data-bs-target={`#nav-${eventKey}`}
                  type="button"
                  role="tab"
                  aria-controls={`nav-${eventKey}`}
                >
                  {title}
                </Nav.Link>
              ))}
          </div>
        </Nav>
        <Tab.Content className="mt-4 iq-tab-fade-up" id="nav-tabContent">
          {tabs?.length > 0 &&
            tabs?.map(({ eventKey, Content }) => (
              <Tab.Pane
                key={eventKey}
                eventKey={eventKey}
                id={`nav-${eventKey}`}
                role="tabpanel"
                aria-labelledby={`nav-${eventKey}`}
              >
                <div className="d-flex flex-column">{<Content />}</div>
              </Tab.Pane>
            ))}
        </Tab.Content>
      </Tab.Container>
    </>
  );
}

const IframeViewer = ({ html, isLoading, loadingHTML }) => {
  if (isLoading || loadingHTML) {
    return (
      <Skeleton
        variant="square"
        width={"100%"}
        height={340}
        className="rounded-3"
      />
    );
  } else if (!isLoading && !html && !loadingHTML) {
    return (
      <PageNotFound
        title="No data found"
        backgroundImage={findIcon("Empty-folder", "dual-tone", 100)}
      />
    );
  }
  return (
    <div>
      <Card>
        <Card.Body className="mb-0 pb-0">
          {html && (
            <iframe
              title="html-viewer"
              style={{
                width: "100%",
                height: "500px",
                border: "1px solid black",
                marginTop: "10px",
              }}
              srcDoc={html}
            />
          )}
        </Card.Body>
      </Card>
    </div>
  );
};
